import { useCallback, useEffect, useState } from "react";

import type { PortalNextSession } from "../domain/types";
import { PortalAPI } from "../infrastructure/portalApi";

/** The reason a cancel was refused, as a translation key, or a generic one. */
function refusalKey(error: unknown): string {
    const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })
        ?.response;
    const detail = response?.data?.detail;
    if (response?.status === 409 && typeof detail === "string") {
        return `portal.nextSession.cancelRefused.${detail}`;
    }
    return "portal.nextSession.cancelError";
}

/**
 * The patient's next session, and cancelling it.
 *
 * A failed lookup reads as "nothing coming": an empty card promising a next
 * session is worse than no card, and there is nothing the patient can do
 * about a network error here.
 */
export function usePortalNextSession() {
    const [session, setSession] = useState<PortalNextSession | null>(null);
    const [cancelling, setCancelling] = useState(false);
    const [cancelled, setCancelled] = useState(false);
    const [refusal, setRefusal] = useState<string | null>(null);

    useEffect(() => {
        PortalAPI.getNextSession()
            .then(({ data }) => setSession(data ?? null))
            .catch(() => setSession(null));
    }, []);

    const cancel = useCallback(async () => {
        if (!session) return;
        setCancelling(true);
        setRefusal(null);
        try {
            await PortalAPI.cancelSession(session.id);
            setCancelled(true);
        } catch (error) {
            setRefusal(refusalKey(error));
        } finally {
            setCancelling(false);
        }
    }, [session]);

    return { session, cancelling, cancelled, refusal, cancel };
}
